import { useState, useEffect } from 'react';
import { Bell, Check, CheckCheck, AlertCircle, Info, CheckCircle, XCircle, Megaphone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { notificationService, type Notification } from '@/services/notificationService';
import { cn } from '@/lib/utils';

interface NotificationListProps {
  limit?: number;
  showMarkAll?: boolean;
  onNotificationClick?: (notification: Notification) => void;
}

export const NotificationList = ({
  limit = 10,
  showMarkAll = true,
  onNotificationClick,
}: NotificationListProps) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [markingAsRead, setMarkingAsRead] = useState<string | null>(null);

  useEffect(() => {
    loadNotifications();
    loadUnreadCount();
  }, []);

  const loadNotifications = async () => {
    try {
      setLoading(true);
      const response = await notificationService.getNotifications({
        limit,
        unreadOnly: false,
      });
      setNotifications(response.data.notifications);
    } catch (error) {
      console.error('Failed to load notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadUnreadCount = async () => {
    try {
      const response = await notificationService.getUnreadCount();
      setUnreadCount(response.data.unreadCount);
    } catch (error) {
      console.error('Failed to load unread count:', error);
    }
  };

  const handleMarkAsRead = async (notificationId: string) => {
    try {
      setMarkingAsRead(notificationId);
      await notificationService.markAsRead(notificationId);
      await loadNotifications();
      await loadUnreadCount();
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    } finally {
      setMarkingAsRead(null);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationService.markAllAsRead();
      await loadNotifications();
      await loadUnreadCount();
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  const getNotificationIcon = (type: Notification['type']) => {
    switch (type) {
      case 'success':
        return CheckCircle;
      case 'error':
        return XCircle;
      case 'warning':
        return AlertCircle;
      case 'announcement':
        return Megaphone;
      default:
        return Info;
    }
  };

  const getNotificationColor = (type: Notification['type']) => {
    switch (type) {
      case 'success':
        return 'text-success bg-success/10 border-success/30';
      case 'error':
        return 'text-destructive bg-destructive/10 border-destructive/30';
      case 'warning':
        return 'text-warning bg-warning/10 border-warning/30';
      case 'announcement':
        return 'text-primary bg-primary/10 border-primary/30';
      default:
        return 'text-info bg-info/10 border-info/30';
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="flex h-32 items-center justify-center">
        <div className="h-6 w-6 animate-spin rounded-full border-b-2 border-primary"></div>
      </div>
    );
  }

  if (notifications.length === 0) {
    return (
      <div className="py-12 text-center">
        <Bell className="mx-auto h-12 w-12 text-muted-foreground" />
        <p className="mt-4 text-sm text-muted-foreground">
          No notifications yet
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {showMarkAll && unreadCount > 0 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            {unreadCount} unread notification{unreadCount !== 1 ? 's' : ''}
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={handleMarkAllAsRead}
            className="text-xs"
          >
            <CheckCheck className="mr-2 h-3 w-3" />
            Mark all as read
          </Button>
        </div>
      )}

      <div className="space-y-2">
        {notifications.map((notification) => {
          const Icon = getNotificationIcon(notification.type);
          const colorClass = getNotificationColor(notification.type);

          return (
            <div
              key={notification.id}
              className={cn(
                'group rounded-lg border p-4 transition-colors',
                notification.isRead
                  ? 'bg-card border-border'
                  : 'bg-accent border-primary/20',
                onNotificationClick && 'cursor-pointer hover:bg-accent/80'
              )}
              onClick={() => onNotificationClick?.(notification)}
            >
              <div className="flex items-start space-x-3">
                <div
                  className={cn(
                    'flex h-10 w-10 items-center justify-center rounded-lg border',
                    colorClass
                  )}
                >
                  <Icon className="h-5 w-5" />
                </div>
                <div className="flex-1 space-y-1">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4
                        className={cn(
                          'text-sm font-medium',
                          !notification.isRead && 'font-semibold'
                        )}
                      >
                        {notification.title}
                      </h4>
                      <p className="mt-1 text-sm text-muted-foreground">
                        {notification.message}
                      </p>
                    </div>
                    {!notification.isRead && (
                      <div className="ml-2 h-2 w-2 rounded-full bg-primary"></div>
                    )}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">
                      {formatDate(notification.sentAt)}
                    </span>
                    {!notification.isRead && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-2 text-xs"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMarkAsRead(notification.id);
                        }}
                        disabled={markingAsRead === notification.id}
                      >
                        {markingAsRead === notification.id ? (
                          <div className="h-3 w-3 animate-spin rounded-full border-b-2 border-primary"></div>
                        ) : (
                          <>
                            <Check className="mr-1 h-3 w-3" />
                            Mark as read
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};






